import { Box, Button, Fieldset, Flex, Grid, Group, Select, Text, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { deviceOptions } from '../../../../../../shared/constants/commonCode';
import { linkTypeOptions, networkTypeOptions } from '../../../../../../shared/constants/options';
import useGetPlans from '../../../../../../shared/hook/queries/useGetPlans';
import { queryClient } from '../../../../../../shared/queryClient';
import { queries } from '../../../../../../shared/queries';
import type { Device, IRegModlinkFormValues } from '../../../../../../shared/types/device';
import type { IModem } from '../../../../../../shared/types/modem';
import ModemListModal from './modemListModal';
import { isRequiredLinkDevice } from '../utils/isRequiredLinkDevice';
import { IconRefresh } from '@tabler/icons-react';

interface IDeviceBasicInfoFormProps {
  register: UseFormRegister<IRegModlinkFormValues>;
  errors: FieldErrors<IRegModlinkFormValues>;
  control: Control<IRegModlinkFormValues>;
  setValue: UseFormSetValue<IRegModlinkFormValues>;
  data: Device | undefined;
}

export default function DeviceBasicInfoForm(props: IDeviceBasicInfoFormProps) {
  const { id } = useParams<{ id: string }>();
  const { register, errors, control, setValue, data } = props;
  const [modemListOpened, { open: openModemList, close: closeModemList }] = useDisclosure(false);
  const deviceType = useWatch({
    control,
    name: 'deviceType',
  });
  const modemId = useWatch({
    control,
    name: 'modemId',
  });
  const isLinkRequired = isRequiredLinkDevice(deviceType);
  const isDisabled = data?.shipment;
  const selectedModemDetails = modemId
    ? queryClient.getQueryData<{ data: IModem }>(queries.modems.modemDetails({ id: Number(modemId) }).queryKey)?.data
    : undefined;
  const selectedModemNo =
    modemId == null
      ? ''
      : data?.modemId != null && String(data.modemId) === modemId
        ? String(data.modemNo)
        : (selectedModemDetails?.modemNo ?? '');

  const handleSelectModem = (modem: IModem) => {
    queryClient.setQueryData(queries.modems.modemDetails({ id: modem.id }).queryKey, {
      data: modem,
    });
    setValue('modemId', String(modem.id), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const { data: plans } = useGetPlans({
    pg: 1,
  });
  const planOptions = plans?.list.map((plan) => ({ value: plan.id.toString(), label: plan.planName })) || [];

  const handleResetModem = () => {
    setValue('modemId', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };
  return (
    <Fieldset
      styles={(theme) => ({
        legend: {
          fontSize: theme.fontSizes.lg,
          fontWeight: 600,
        },
      })}
      legend="디바이스 정보 입력"
      variant="unstyled"
    >
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="MAC"
            disabled={!!id}
            placeholder="MAC을 입력하세요."
            error={errors.mac?.message}
            {...register('mac', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                e.target.value = e.target.value.toUpperCase();
              },
            })}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Box data-field="deviceType">
            <Controller
              name="deviceType"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  key={field.value}
                  withAsterisk
                  label="디바이스 종류"
                  placeholder="디바이스 종류를 선택하세요."
                  data={deviceOptions}
                  error={fieldState.error?.message}
                  disabled={isDisabled}
                />
              )}
            />
          </Box>
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label="S/N"
            error={errors.serialNum?.message}
            placeholder="시리얼 넘버를 입력하세요."
            disabled={isDisabled}
            {...register('serialNum')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Flex gap="xs">
            <Box data-field="link1" flex={1}>
              <Controller
                name="link1"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    key={field.value}
                    flex={1}
                    clearable
                    withAsterisk={isLinkRequired}
                    label="링크1 종류"
                    placeholder="링크 종류를 선택하세요."
                    data={linkTypeOptions}
                    error={fieldState.error?.message}
                    disabled={isDisabled}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Box>
            <Box data-field="link2" flex={1}>
              <Controller
                name="link2"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    key={field.value}
                    flex={1}
                    clearable
                    withAsterisk={isLinkRequired}
                    label="링크2 종류"
                    placeholder="링크 종류를 선택하세요."
                    data={linkTypeOptions}
                    error={fieldState.error?.message}
                    disabled={isDisabled}
                    onChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Box>
          </Flex>
        </Grid.Col>
        <Grid.Col span={6}>
          <Controller
            name="planId"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                {...field}
                key={field.value}
                searchable
                clearable
                label="서비스 요금제"
                placeholder="서비스 요금제를 선택하세요."
                data={planOptions}
                error={fieldState.error?.message}
                disabled={isDisabled}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="영업 담당자"
            placeholder="영업 담당자를 입력하세요."
            disabled={isDisabled}
            {...register('salesManager')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Box data-field="networkType">
            <Controller
              name="networkType"
              control={control}
              render={({ field, fieldState }) => (
                <Box pos="relative">
                  <Select
                    {...field}
                    key={field.value}
                    withAsterisk
                    label="네트워크 종류"
                    placeholder="네트워크 종류를 선택하세요."
                    data={networkTypeOptions}
                    error={fieldState.error?.message}
                    disabled={isDisabled}
                  />
                  <Text size="xs" c="dimmed" pos="absolute" top={0} left={92} mt={4}>
                    (ETHERNET, WIFI 인 경우, 모뎀/라우터 정보 필요없음)
                  </Text>
                </Box>
              )}
            />
          </Box>
        </Grid.Col>
        <Grid.Col span={6}>
          <Group align="flex-end">
            <TextInput
              readOnly
              flex={1}
              label="단말기 일련번호"
              placeholder="연결할 단말기를 선택하세요."
              value={selectedModemNo}
              onClick={openModemList}
              styles={() => ({
                input: {
                  cursor: 'pointer',
                  caretColor: 'transparent',
                },
              })}
            />
            <Button onClick={openModemList}>검색</Button>
            <Button leftSection={<IconRefresh size={14} />} onClick={handleResetModem} disabled={!modemId} variant="transparent">
              초기화
            </Button>
          </Group>
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="비고" placeholder="비고를 입력하세요." disabled={isDisabled} {...register('memo')} />
        </Grid.Col>
      </Grid>
      {modemListOpened && (
        <ModemListModal opened={modemListOpened} onClose={() => closeModemList()} onSelectModem={handleSelectModem} />
      )}
    </Fieldset>
  );
}
